/**
 * When a film is out *here* — cities plan C2.3.
 *
 * A film opens on a different day in every country, and the app had one:
 * `film.de_release_on`, TMDb's German theatrical date. In Boston that date is
 * the wrong one — a film "new in town" because Germany got it a fortnight ago
 * is not new at the Coolidge. The Worker writes a map, `film.release_on`,
 * keyed by the ISO country (`{ "DE": "2026-10-01", "US": "2026-09-26" }`,
 * C4.3), and this reads the current region's country out of it.
 *
 * `de_release_on` is the Berlin fallback and **only** the Berlin one: the
 * map has not been written for the films already in the table, and until it
 * is, Germany's date still answers for Germany. It is never lent to another
 * country — that would be the one value doing two jobs (*the German release*
 * and *the release*), and in Boston it would be silently wrong, where null
 * is honestly "nobody told us".
 *
 * Ambient country, like `zone.ts`'s zone and `currency.ts`'s currency: set
 * by `currentRegion.ts`'s `RegionRoot` before any child reads it, so
 * `newIn.ts`, `browse.ts` and `browseRank.ts` (import-free, called from
 * everywhere) can ask. Until then it is Germany's, which is what they read
 * before this file existed and what the Worker's suite reads.
 */

/** The film fields this reads, declared rather than imported. */
export interface FilmReleases {
  release_on?: Record<string, string | null | undefined> | null
  de_release_on?: string | null
}

/** Berlin's country, and the one `de_release_on` is a fallback for. */
export const FALLBACK_COUNTRY = 'DE'

let country = FALLBACK_COUNTRY

/** Installed by `currentRegion.ts`. Idempotent. */
export function setHereCountry(next: string): void {
  country = next || FALLBACK_COUNTRY
}

/** The current region's ISO country. */
export function hereCountry(): string {
  return country
}

/** The film's release date in `forCountry` (default: the current region's),
 *  `YYYY-MM-DD`, or null where none is known. */
export function releaseOn(film: FilmReleases | null | undefined, forCountry: string = country): string | null {
  if (!film) return null
  const map = film.release_on
  // `hasOwn`, not a bare index: the map is jsonb off the wire and a country
  // code is a lookup key, not a license to read `Object.prototype`.
  const hit = map && Object.hasOwn(map, forCountry) ? map[forCountry] : null
  if (hit) return hit
  return forCountry === FALLBACK_COUNTRY ? film.de_release_on || null : null
}
